import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth, canWrite } from "@/context/AuthContext";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from "recharts";
import { AlertTriangle, FilePlus2, PackageX } from "lucide-react";
import { api, eur, dt, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Kpi, Panel, QuotaBar, Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { ConsigliScarto } from "@/components/ConsigliScarto";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const LowStock = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/magazzino/sottoscorta").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <div data-testid="low-stock-alert" className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      <div className="mb-2 flex items-center gap-2 font-semibold"><PackageX className="h-4 w-4" />{rows.length === 1 ? "1 articolo sotto la scorta minima" : `${rows.length} articoli sotto la scorta minima`}
        <Link to="/app/magazzino" className="ml-auto text-xs underline" data-testid="low-stock-link">Vai al magazzino</Link></div>
      <ul className="space-y-1">
        {rows.map((r) => <li key={r.product_id} data-testid={`low-stock-row-${r.product_id}`} className="flex justify-between gap-4"><span>{r.codice && <span className="font-mono text-xs">{r.codice} · </span>}{r.descrizione}</span><span className="font-mono">{r.giacenza.toLocaleString("it-IT")} / min {r.scorta_minima.toLocaleString("it-IT")} {r.unita}</span></li>)}
      </ul>
    </div>
  );
};

const Scarti = ({ items, canFix }) => {
  const [rows, setRows] = useState(items);
  if (!rows.length) return null;
  const risolto = async (id) => {
    try { await api.post(`/invoices/${id}/scarto-risolto`); setRows(rows.filter((r) => r.id !== id)); toast.success("Tolta dall'elenco degli scarti"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="Fatture scartate da correggere">
      <ul data-testid="scarti-list" className="divide-y divide-slate-100">
        {rows.map((i) => (
          <li key={i.id} data-testid={`scarto-row-${i.id}`} className="flex flex-wrap items-center gap-3 py-2.5 text-sm">
            <Link to={`/app/fatture/${i.id}`} data-testid={`scarto-open-${i.id}`} className="min-w-0 flex-1 rounded px-1 transition-colors hover:bg-rose-50">
              <div className="flex flex-wrap justify-between gap-2"><span className="font-medium text-slate-800">n. {i.numero} · {dt(i.data)} · {i.controparte}</span><span className="font-mono font-semibold">{eur(i.importo)}</span></div>
              <div className="text-xs text-rose-700">{i.motivo}</div>
              <ConsigliScarto note={i.motivo} testid={`scarto-consigli-${i.id}`} className="bg-slate-50" />
            </Link>
            {canFix && <Button size="sm" variant="outline" data-testid={`scarto-risolto-${i.id}`} onClick={() => risolto(i.id)}>Risolto</Button>}
          </li>
        ))}
      </ul>
    </Panel>
  );
};

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const DueList = ({ items, base, today, testid }) =>
  items.length === 0 ? (
    <Empty text="Nessuna scadenza aperta" />
  ) : (
    <ul data-testid={testid} className="divide-y divide-slate-100">
      {items.map((i) => (
        <li key={i.id}>
          <Link to={`${base}/${i.id}`} className="flex items-center justify-between gap-4 py-2.5 text-sm transition-colors hover:bg-slate-50">
            <div className="min-w-0">
              <div className="truncate font-medium text-slate-800">{i.controparte}</div>
              <div className="text-xs text-slate-500">n. {i.numero} · {dt(i.data)}</div>
            </div>
            <div className="text-right">
              <div className="font-mono font-semibold">{eur(i.importo)}</div>
              <div className={`text-xs ${i.scadenza && i.scadenza < today ? "font-semibold text-rose-600" : "text-slate-500"}`}>{i.rata ? `rata ${i.rata} · ` : ""}scad. {dt(i.scadenza)}</div>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );

export default function Dashboard() {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState(null);

  useEffect(() => {
    api.get("/dashboard", { params: { year } }).then((r) => setD(r.data));
  }, [year]);

  if (!d) return <div className="text-sm text-slate-500">Caricamento…</div>;
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const chart = d.months.map((m) => ({ ...m, mese: MESI[m.mese - 1] }));

  return (
    <div className="space-y-8">
      <PageHeader title="Dashboard" subtitle={`Andamento ${d.year}: fatturato, incassi e scadenze`}>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger data-testid="dashboard-year-select" className="w-28 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        {canWrite(user) && <Button asChild data-testid="dashboard-new-invoice" className="bg-sky-600 hover:bg-sky-700">
          <Link to="/app/fatture/nuova"><FilePlus2 className="mr-2 h-4 w-4" />Nuova fattura</Link>
        </Button>}
      </PageHeader>

      {(d.bozze > 0 || d.scartate > 0) && (
        <div data-testid="dashboard-alert" className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4" />
          {d.bozze > 0 && <span>{d.bozze === 1 ? "1 bozza da trasmettere." : `${d.bozze} bozze da trasmettere.`}</span>}
          {d.scartate > 0 && <span>{d.scartate === 1 ? "1 documento scartato o non consegnato da verificare." : `${d.scartate} documenti scartati o non consegnati da verificare.`}</span>}
        </div>
      )}
      <LowStock />
      <Scarti key={d.year} items={d.scarti || []} canFix={canWrite(user)} />

      <div className="stagger grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi testid="kpi-fatturato" label="Fatturato" value={eur(d.fatturato)} hint={`${d.count_out} documenti emessi`} />
        <Kpi testid="kpi-incassato" label="Incassato" value={eur(d.incassato)} tone="green" />
        <Kpi testid="kpi-da-incassare" label="Da incassare" value={eur(d.da_incassare)} tone="blue" />
        <Kpi testid="kpi-scaduto" label="Scaduto" value={eur(d.scaduto)} tone="red" hint="Crediti oltre la scadenza" />
      </div>
      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <Kpi testid="kpi-iva-debito" label="IVA a debito" value={eur(d.iva_debito)} />
        <Kpi testid="kpi-iva-credito" label="IVA a credito" value={eur(d.iva_credito)} />
        <Kpi testid="kpi-costi" label="Costi (ricevute)" value={eur(d.costi)} hint={`${d.count_in} documenti ricevuti`} />
        <Kpi testid="kpi-da-pagare" label="Da pagare" value={eur(d.da_pagare)} tone="amber" />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Emesse vs ricevute per mese" className="lg:col-span-2">
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="mese" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={70} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `€${(v / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })}k` : `€${Math.round(v)}`)} />
                <Tooltip formatter={(v) => eur(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="emesse" name="Emesse" fill="#0284C7" radius={[3, 3, 0, 0]} />
                <Bar dataKey="ricevute" name="Ricevute" fill="#94A3B8" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Pacchetto XML">
          <QuotaBar used={d.xml_used} quota={d.xml_quota} />
          <p className="mt-4 text-xs leading-relaxed text-slate-500">Ogni fattura trasmessa allo SdI consuma un documento del pacchetto. Per una ricarica contatta lo studio principale.</p>
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Scadenze clienti (da incassare)">
          <DueList testid="due-active-list" items={d.scadenze_attive} base="/app/fatture" today={d.today} />
        </Panel>
        <Panel title="Scadenze fornitori (da pagare)">
          <DueList testid="due-passive-list" items={d.scadenze_passive} base="/app/ricevute" today={d.today} />
        </Panel>
      </div>
    </div>
  );
}
